import { fetchWithAuth } from "@/lib/fetchWithAuth";

const apiUrl = () => process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

type Raw = Record<string, unknown>;

const record = (value: unknown): Raw =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Raw)
    : {};

const arrayItems = (value: unknown): unknown[] => {
  const root = record(value);
  if (Array.isArray(value)) return value;
  if (Array.isArray(root.items)) return root.items;
  if (root.data !== undefined) {
    const data = root.data;
    if (Array.isArray(data)) return data;
    const dataRoot = record(data);
    if (Array.isArray(dataRoot.items)) return dataRoot.items;
  }
  return [];
};

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const objectData = (value: unknown): Raw => {
  const root = record(value);
  return root.data !== undefined ? record(root.data) : root;
};

export type SiteWorkspaceRole = "OWNER" | "ADMIN" | "EDITOR" | "VIEWER";

export type SiteWorkspace = {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  siteManager: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  role: SiteWorkspaceRole;
  createdAt: string | null;
  updatedAt: string | null;
};

export type SiteWorkspaceInput = {
  name: string;
  description?: string | null;
  location?: string | null;
  siteManager?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

let siteWorkspacesInFlight: Promise<SiteWorkspace[]> | null = null;

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

function apiError(payload: unknown, fallback: string): string {
  const root = record(payload);
  const message = root.message;
  if (typeof message === "string" && message.trim()) return message;
  const error = record(root.error);
  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function normalizeRole(value: unknown): SiteWorkspaceRole {
  return value === "OWNER" ||
    value === "ADMIN" ||
    value === "EDITOR" ||
    value === "VIEWER"
    ? value
    : "VIEWER";
}

function normalizeSite(value: unknown): SiteWorkspace | null {
  const item = record(value);
  const id = text(item.siteId) ?? text(item.id);
  const name = text(item.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    description: text(item.description),
    location: text(item.location),
    siteManager: text(item.siteManager),
    startDate: text(item.startDate),
    endDate: text(item.endDate),
    status: text(item.status) ?? "active",
    role: normalizeRole(item.role),
    createdAt: text(item.createdAt),
    updatedAt: text(item.updatedAt),
  };
}

/**
 * Product contract: a Site belongs to its owner/collaborators, not to a Team.
 * The backend returns all sites the signed-in user owns or participates in.
 */
export function fetchSiteWorkspaces(): Promise<SiteWorkspace[]> {
  if (siteWorkspacesInFlight) return siteWorkspacesInFlight;

  siteWorkspacesInFlight = (async () => {
    const sites: SiteWorkspace[] = [];
    let page = 1;
    let total = Number.POSITIVE_INFINITY;

    while (sites.length < total) {
      const response = await fetchWithAuth(
        `${apiUrl()}/api/platform/sites?limit=100&page=${page}`,
        { skipRedirectOn401: false },
      );
      const payload = await readJson(response);
      if (!response.ok) {
        throw new Error(
          apiError(payload, "현장 프로젝트를 불러오지 못했습니다."),
        );
      }

      const batch = arrayItems(payload).flatMap<SiteWorkspace>((value) => {
        const site = normalizeSite(value);
        return site ? [site] : [];
      });
      sites.push(...batch);

      const root = objectData(payload);
      const parsedTotal = Number(root.total ?? sites.length);
      total =
        Number.isFinite(parsedTotal) && parsedTotal >= 0
          ? parsedTotal
          : sites.length;
      if (batch.length < 100 || batch.length === 0) break;
      page += 1;
    }

    return sites.sort((a, b) => {
      const left = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
      const right = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
      return right - left;
    });
  })().finally(() => {
    siteWorkspacesInFlight = null;
  });

  return siteWorkspacesInFlight;
}

export async function fetchSiteWorkspace(
  siteId: string,
): Promise<SiteWorkspace> {
  const response = await fetchWithAuth(
    `${apiUrl()}/api/platform/sites/${encodeURIComponent(siteId)}`,
    { skipRedirectOn401: false },
  );
  const payload = await readJson(response);
  if (!response.ok) {
    throw new Error(apiError(payload, "현장 프로젝트를 불러오지 못했습니다."));
  }
  const site = normalizeSite(objectData(payload));
  if (!site) throw new Error("현장 프로젝트 정보가 올바르지 않습니다.");
  return site;
}

export async function createSiteWorkspace(
  input: SiteWorkspaceInput,
): Promise<SiteWorkspace> {
  const body = Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== null && value !== ""),
  );
  const response = await fetchWithAuth(`${apiUrl()}/api/platform/sites`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    skipRedirectOn401: false,
  });
  const payload = await readJson(response);
  if (!response.ok) {
    throw new Error(apiError(payload, "현장 프로젝트를 만들지 못했습니다."));
  }
  const site = normalizeSite(objectData(payload));
  if (!site) throw new Error("생성된 현장 정보를 확인할 수 없습니다.");
  return site;
}

export async function updateSiteWorkspace(
  siteId: string,
  input: SiteWorkspaceInput,
): Promise<void> {
  const body = Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  );
  const response = await fetchWithAuth(
    `${apiUrl()}/api/platform/sites/${encodeURIComponent(siteId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      skipRedirectOn401: false,
    },
  );
  const payload = await readJson(response);
  if (!response.ok) {
    throw new Error(apiError(payload, "현장 프로젝트를 수정하지 못했습니다."));
  }
}

export async function archiveSiteWorkspace(siteId: string): Promise<void> {
  const response = await fetchWithAuth(
    `${apiUrl()}/api/platform/sites/${encodeURIComponent(siteId)}`,
    { method: "DELETE", skipRedirectOn401: false },
  );
  const payload = await readJson(response);
  if (!response.ok) {
    throw new Error(apiError(payload, "현장 프로젝트를 보관하지 못했습니다."));
  }
}
